/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Chip, CircularProgress, Box, TextField, Tooltip } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Delete, EditNote, MenuBook } from "@mui/icons-material";
// import type { Users as UsersType, WrapTextCellProps } from "types";
import toast from "react-hot-toast";
import { usePagePermissions } from "../../Components/PagePermission";
import { BASE_API_URL } from "../../API/Base";
import AxiosInstance from "../../API/axios";
import Layout from "../../Components/Layout";
import AddItemButton from "../../Components/AddItemButton";
import DeleteModal from "../../Modals/DeleteModal";
import { WrapTextCellProps, Users as UsersType } from "../../types";

const Users: React.FC = () => {
  const [rows, setRows] = useState<UsersType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [rowCount, setRowCount] = useState<number>(0);
  const [searchValue, setSearchValue] = useState<string>("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>("");

  const navigate = useNavigate();
  // const theme: Theme = useThemeStore((state) => state.theme);

  // Fetch permissions for User module
  const permissions = usePagePermissions(3);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchValue]);

  // const ActionNamesCell = ({ value }: { value: string[] }) => (
  //   <div style={{ display: "flex", flexWrap: "wrap" }}>
  //     {value.map((actionName) => (
  //       <Chip
  //         key={actionName}
  //         label={actionName}
  //         color="info"
  //         variant="outlined"
  //         size="small"
  //         style={{
  //           margin: 4,
  //           whiteSpace: "nowrap",
  //           borderRadius: 0,
  //         }}
  //       />
  //     ))}
  //   </div>
  // );
  function base64Encode(str: string) {
    return btoa(str);
  }

  // Base64 decoding function
  function base64Decode(encodedStr: string) {
    return atob(encodedStr);
  }

  const fetchData = async (
    page: number,
    pageSize: number,
    searchValue: string
  ) => {
    try {
      setLoading(true);
      const response = await AxiosInstance.get(
        `${BASE_API_URL}/admin/users/v1/users`,
        {
          params: {
            page,
            per_page: pageSize,
            search_query: searchValue,
          },
        }
      );

      const usersData = response.data.users_data;

      if (!response.data || !usersData || response.data.total_count == null) {
        console.error("Invalid response format:", response.data);
        setLoading(false);
        return;
      }

      const formattedData = usersData.map((row: UsersType, index: number) => ({
        ...row,
        actionsID: startIndex + index + 1, // Ensure startIndex is defined
        Id: row.Id,
      }));

      setRows(formattedData);
      setRowCount(response.data.total_count);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false); // Set loading to false in case of an error
    }
  };

  useEffect(() => {
    fetchData(currentPage, pageSize, debouncedSearchValue);
  }, [currentPage, pageSize, debouncedSearchValue]);

  // Calculate total pages
  const totalPages = Math.ceil(rowCount / pageSize);

  // Calculate start and end index for the visible rows
  const startIndex = (currentPage - 1) * pageSize;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Add logic to fetch data for the new page or perform any other actions
  };

  const WrapTextCell: React.FC<WrapTextCellProps> = ({ value }) => {
    return <div style={{ whiteSpace: "pre-wrap" }}>{value}</div>;
  };

  const handleAdd = () => {
    navigate("/user/manage/form/create");
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
  };

  const handleView = (row: any) => {
    const encodedId = base64Encode(row.Id);
    navigate(`/user/manage/form/view/${encodedId}`);

    const actionId = permissions?.View?.id;
    const decodedId = base64Decode(encodedId);
    // logEvent(3, actionId, decodedId);
  };

  const handleEdit = (row: any) => {
    const encodedId = base64Encode(row.Id);
    navigate(`/user/manage/form/edit/${encodedId}`);
  };

  // Conditionally define the Action column based on permissions
  const actionColumn =
    permissions?.Update || permissions?.Delete || permissions?.View
      ? {
          field: "action",
          headerName: "Action",
          width: 120,
          renderCell: (params: any) => (
            <div className="flex flex-row gap-x-1">
              {permissions?.Update && (
                <div
                  className="px-1.5 py-0.5 rounded-md text-Edit cursor-pointer"
                  onClick={() => {
                    handleEdit(params.row);
                  }}
                >
                  <Tooltip title="Edit">
                    <EditNote sx={{ fontSize: "20px" }} />
                  </Tooltip>
                </div>
              )}
              {permissions?.Delete && (
                <div
                  className="px-1.5 py-0.5 rounded-md text-Delete cursor-pointer"
                  onClick={() => {
                    handleDelete(params.row.Id);
                    const actionId = permissions?.Delete?.id;
                  }}
                >
                  <Tooltip title="Delete">
                    <Delete sx={{ fontSize: "20px" }} />
                  </Tooltip>
                </div>
              )}
              {permissions?.View && (
                <div
                  className="px-1.5 py-0.5 rounded-md text-View cursor-pointer flex flex-row items-center justify-center"
                  onClick={() => {
                    handleView(params.row);
                    const actionId = permissions?.View?.id;
                  }}
                >
                  <Tooltip title="View">
                    <MenuBook sx={{ fontSize: "20px" }} />
                  </Tooltip>
                </div>
              )}
            </div>
          ),
        }
      : undefined;

  const coloumns: GridColDef[] = [
    {
      field: "actionsID",
      headerName: "ID",
      width: 70,
      renderCell: (params) => (
        <div className="cellWithStatus">{params.value}</div>
      ),
    },
    ...(actionColumn ? [actionColumn] : []),

    {
      field: "User_Name",
      headerName: "Username",
      cellClassName: "capitalize",
      width: 160,

      renderCell: (params) => (
        <div className="cellWithStatus">{params.value}</div>
      ),
    },
    {
      field: "Email_Id",
      headerName: "Email ID",
      width: 250,

      renderCell: (params) => (
        <div className={`cellWithStatus ${params.value ? "" : "inactive"}`}>
          {params.value ? <WrapTextCell value={params.value} /> : "N/A"}
        </div>
      ),
    },
    {
      field: "Mobile_No",
      headerName: "Mobile number",
      cellClassName: "capitalize",
      width: 160,

      renderCell: (params) => (
        <div className={`cellWithStatus ${params.value ? "" : "inactive"}`}>
          {params.value ? <WrapTextCell value={params.value} /> : "N/A"}
        </div>
      ),
    },

    {
      field: "IsStatus",
      headerName: "Status",
      cellClassName: "capitalize",
      width: 120,

      renderCell: (params) => (
        <div
          className={`cellWithStatus ${params.value ? "active" : "inactive"}`}
        >
          {params.value ? "Active" : "Inactive"}
        </div>
      ),
    },
  ];

  const filteredUserData = rows.filter(
    (row: any) =>
      row.Email_Id.toLowerCase().includes(searchValue.toLowerCase()) ||
      row.User_Name.toLowerCase().includes(searchValue.toLowerCase()) ||
      row.User_Type_Name.toLowerCase().includes(searchValue.toLowerCase())
  );

  const handleDelete = (id: string) => {
    const encodedId = base64Encode(id);
    setIsDeleteModalOpen(true);
    setSelectedRowId(encodedId);
  };

  const handleDeleteConfirm = async () => {
    try {
      const decodedId = selectedRowId ? base64Decode(selectedRowId) : null;

      const response = await AxiosInstance.delete(
        `${BASE_API_URL}/admin/users/v1/delete-user/${decodedId}`
      );

      if (response.data) {
        // Remove the deleted row from the local state
        setIsDeleteModalOpen(false);
        setRows((prevRows) =>
          prevRows.filter((row: any) => row.Id.toString() !== decodedId)
        );
        toast.success("Data deleted successfully");
        const actionId = permissions?.Delete?.id;
        // logEvent(3, actionId, decodedId);
      } else {
        toast.error("Error deleting data");
      }
    } catch (error) {
      console.error("Error deleting data:", error);
      setIsDeleteModalOpen(false);
    }
  };

  return (
    <Layout PageId={1}>
      <div className="flex flex-col items-center justify-start w-full h-full">
        <div
          id="actions"
          className="flex flex-col md:flex-row items-center justify-between w-full gap-x-2"
        >
          {/* <div className="flex flex-row items-center justify-center md:w-1/4 w-full ">
            <SearchInput
              value={searchValue}
              placeholder={"Search"}
              onChange={setSearchValue}
            />
          </div> */}
          <div className="flex md:justify-end md:space-x-2 justify-between items-center w-full ">
            {permissions.Add && (
              <AddItemButton label="Add new" onClick={handleAdd} size="small" />
            )}
            {/* <ActionButtonsWithDropdown /> */}
          </div>
        </div>

        <div className="flex flex-col items-start justify-start w-full mt-2">
          <div className="w-full">
            <Box>
              <DataGrid
                rows={filteredUserData}
                columns={coloumns}
                getRowId={(row) => row.Id}
                autoHeight
                loading={loading}
                // density="comfortable"
              />
            </Box>
          </div>
        </div>
        {/* <PaginationButtons
          currentPage={currentPage}
          totalPages={totalPages}
          handlePageChange={handlePageChange}
        /> */}
      </div>
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Confirm Deletion"
        message="Are you sure you want to delete this data?"
      />
    </Layout>
  );
};

export default Users;
